import React, {Dispatch, forwardRef, SetStateAction} from 'react';
import {
    defaultDropDownV2Context,
    DropdownSelectTrigger,
    DropdownV2,
    DropDownV2Context,
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    TreeList,
    TreeNodeProps
} from '@priority-software/priority-style-react';
import {emailDialogID} from '../../../../../../testIDs';
import styles from './styles.module.scss';
import cx from 'classnames';
import {useTreeDropdown} from '../../hooks/useTreeDropdown';
import {texts} from '../../../../../../texts';
import {LabelWrapper} from '../../../../LabelWrapper';
import {shouldAddDots} from '../../../../../RichText/parts/ToolbarPlugin/utils';
import {TPersonalizeDropdownHandle, TPersonalizeStates} from '../../hooks/types';
import {createDataTestId} from '../../../../../../../../utils/dataTestId';

export interface ITreeDropdownProps {
    setPersonalizeStates: Dispatch<SetStateAction<TPersonalizeStates>>;
}

export const TreeDropdown = forwardRef<TPersonalizeDropdownHandle, ITreeDropdownProps>(
    ({setPersonalizeStates}, ref) => {
        const {
            closeDropdown,
            isOpen,
            openDropdown,
            toggleOpen,
            searchValue,
            onSearchChange,
            handleSelect,
            handleExpandChange,
            selectedBranch,
            branches,
            isRTL
        } = useTreeDropdown({setPersonalizeStates, ref});

        const filterTree = (nodes: TreeNodeProps[]): TreeNodeProps[] => {
            // extraData is used here as a flag to indicate that the node should be displayed as for the search query
            return nodes
                .filter(node => node.extraData.shouldDisplay)
                ?.map(node => ({
                    ...node,
                    nodes: node.nodes ? filterTree(node.nodes) : undefined,
                    onExpandChange: handleExpandChange
                }));
        };

        return (
            <DropDownV2Context.Provider
                value={{
                    ...defaultDropDownV2Context,
                    isOpen,
                    toggleOpen,
                    closeDropdown,
                    openDropdown
                }}
            >
                <LabelWrapper label={texts.tree_dropdown_label}>
                    <DropdownV2
                        {...createDataTestId(`email-dynamic-values-tree-dropdown-container`, true)}
                        onClose={closeDropdown}
                        isOpen={isOpen}
                        width="fullWidth"
                        contentStyle={{[isRTL ? 'right' : 'left']: 10}}
                        portalSelector={emailDialogID}
                        contentComponentClasses={cx(
                            styles.dropdownMenu,
                            styles.hasSearch,
                            styles.hasExtraPadding
                        )}
                        usePortal
                        trigger={
                            <DropdownSelectTrigger
                                {...createDataTestId(
                                    `email-dynamic-values-tree-dropdown-trigger`,
                                    true
                                )}
                                componentClasses={styles.fullWidth}
                                size="large"
                                placeholder={
                                    selectedBranch?.title ? null : texts.tree_dropdown_placeholder
                                }
                                beforeValue={shouldAddDots(selectedBranch?.title || '', 15)}
                            />
                        }
                    >
                        {isOpen && (
                            <div>
                                <div
                                    className={styles.search}
                                    {...createDataTestId(
                                        `email-dynamic-values-tree-dropdown-search-container`,
                                        false
                                    )}
                                >
                                    <Input
                                        {...createDataTestId(
                                            `email-dynamic-values-tree-dropdown-search-input`,
                                            true
                                        )}
                                        autoFocus
                                        roundedCorners
                                        value={searchValue}
                                        onChange={onSearchChange}
                                        placeholder={texts.choose_dropdown_search_placeholder}
                                    >
                                        <Icon
                                            {...createDataTestId(
                                                `email-dynamic-values-tree-dropdown-search-icon`,
                                                true
                                            )}
                                            componentClasses={styles.searchIcon}
                                            icon={PriorityIcon.searchThin}
                                            iconColor="secondary-400"
                                        />
                                    </Input>
                                </div>
                                <Scrollbar autoHeight autoHeightMax={300} autoHeightMin={300}>
                                    <TreeList
                                        {...createDataTestId(
                                            `email-dynamic-values-tree-dropdown-list-container`,
                                            true
                                        )}
                                        onSelect={handleSelect}
                                        nodes={filterTree(branches)}
                                        isRTL={isRTL}
                                    />
                                </Scrollbar>
                            </div>
                        )}
                    </DropdownV2>
                </LabelWrapper>
            </DropDownV2Context.Provider>
        );
    }
);
